import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { useEntitlement } from "@/context/EntitlementContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, Building2, MapPin, RefreshCcw, ExternalLink, Lock, Crown, ArrowRight, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const inputRef = useRef();
  const { active: isPro } = useEntitlement();
  const [profile, setProfile] = useState(null);
  const [filename, setFilename] = useState("");
  const [uploading, setUploading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [totalGenerated, setTotalGenerated] = useState(0);
  const [selected, setSelected] = useState({});
  const [discovering, setDiscovering] = useState(false);
  const [location, setLocation] = useState("");
  const [hint, setHint] = useState("");
  const [count, setCount] = useState(12);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes] = await Promise.all([api.get("/cv/profile"), api.get("/companies")]);
        if (pRes.data.profile) { setProfile(pRes.data.profile); setFilename(pRes.data.filename || ""); }
        setCompanies(cRes.data.companies || []);
        setTotalGenerated(cRes.data.total_generated || (cRes.data.companies?.length || 0));
      } catch (e) { if (e.response?.status === 401) nav("/login"); }
    })();
  }, [nav]);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/cv/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data.profile); setFilename(data.filename);
      toast.success("CV parsed successfully");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/companies/discover", {
        location: location || null, count: Number(count) || 12, extra_hint: hint || null,
      });
      setCompanies(data.companies); setTotalGenerated(data.total_generated || data.companies.length);
      setSelected({});
      toast.success(`Found ${data.total_generated} companies${!isPro && data.upgrade_required ? ` · showing top ${data.returned}` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDiscovering(false); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleAll = () => {
    if (selectedIds.length === companies.length) setSelected({});
    else { const all = {}; companies.forEach((c) => (all[c.id] = true)); setSelected(all); }
  };

  const goReview = () => {
    if (selectedIds.length === 0) return toast.error("Select at least one company");
    sessionStorage.setItem("aiapply_selected", JSON.stringify(selectedIds));
    nav("/app/review");
  };

  const hiddenCount = totalGenerated - companies.length;

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-10">
          <div className="overline">Discover</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            Find companies. Send applications.
          </h1>
        </div>

        {/* CV */}
        <section className="mb-10">
          <div className="overline mb-3">01 · Your CV</div>
          {!profile ? (
            <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                 onDragLeave={() => setDragOver(false)} onDrop={onDrop}
                 onClick={() => inputRef.current?.click()}
                 className={`dropzone cursor-pointer border-2 border-dashed border-[#C7D6CC] rounded-xl p-14 text-center ${dragOver ? "drag-over" : ""}`}
                 data-testid="upload-dropzone">
              <input ref={inputRef} type="file" hidden accept=".pdf,.docx,.txt" data-testid="upload-input"
                     onChange={(e) => handleFile(e.target.files?.[0])} />
              <Upload className="w-8 h-8 mx-auto text-[#2D4A3E]" strokeWidth={1.5} />
              <div className="mt-4 font-display font-semibold text-lg tracking-tight">{uploading ? "Parsing your CV…" : "Drop your CV here"}</div>
              <div className="text-sm text-[#5C6661] mt-1">PDF, DOCX or TXT · we parse role, industry and skills</div>
              {uploading && <div className="mt-3 text-xs text-[#2D4A3E] animate-soft-pulse">Reading with Claude Sonnet 4.5…</div>}
            </div>
          ) : (
            <Card className="p-6 border-[#E2E8E4]" data-testid="cv-profile-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-[#E7F0EB] flex items-center justify-center"><FileText className="w-5 h-5 text-[#2D4A3E]" /></div>
                  <div>
                    <div className="font-display font-semibold text-lg tracking-tight" data-testid="cv-name">{profile.full_name || "Candidate"}</div>
                    <div className="text-sm text-[#5C6661]">{profile.current_role} · {profile.industry}</div>
                    {profile.location && <div className="text-xs text-[#5C6661] mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {profile.location}</div>}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full border-[#E2E8E4]" onClick={() => inputRef.current?.click()} data-testid="replace-cv-btn"><RefreshCcw className="w-3.5 h-3.5 mr-2" /> Replace CV</Button>
                <input ref={inputRef} type="file" hidden accept=".pdf,.docx,.txt" onChange={(e) => handleFile(e.target.files?.[0])} />
              </div>
              {profile.summary && <p className="text-sm text-[#5C6661] mt-5 leading-relaxed">{profile.summary}</p>}
              <div className="flex flex-wrap gap-1.5 mt-5">
                {(profile.skills || []).slice(0, 12).map((s) => (
                  <Badge key={s} variant="secondary" className="bg-[#E7F0EB] text-[#2D4A3E] border-transparent font-normal">{s}</Badge>
                ))}
              </div>
              <div className="text-xs text-[#5C6661] mt-4">Uploaded: {filename}</div>
            </Card>
          )}
        </section>

        {profile && (
          <section className="mb-10">
            <div className="overline mb-3">02 · Discover matching companies</div>
            <Card className="p-6 border-[#E2E8E4]">
              <div className="grid md:grid-cols-4 gap-4">
                <div><Label className="overline">Location</Label>
                  <Input placeholder="e.g. London, remote" value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1.5 h-10 rounded-lg" data-testid="discover-location" /></div>
                <div><Label className="overline">Count</Label>
                  <Input type="number" min="5" max="24" value={count} onChange={(e) => setCount(e.target.value)} className="mt-1.5 h-10 rounded-lg" data-testid="discover-count" /></div>
                <div className="md:col-span-2"><Label className="overline">Hint (optional)</Label>
                  <Input placeholder="e.g. climate tech, fintech, product design" value={hint} onChange={(e) => setHint(e.target.value)} className="mt-1.5 h-10 rounded-lg" data-testid="discover-hint" /></div>
              </div>
              <Button onClick={discover} disabled={discovering} className="mt-5 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="discover-btn">
                {discovering ? <span className="animate-soft-pulse">Finding companies…</span> : <><Sparkles className="w-4 h-4 mr-2" /> Discover companies</>}
              </Button>
              {!isPro && <div className="mt-3 text-xs text-[#5C6661]">Free plan shows the top {5} matches. <Link to="/pricing" className="text-[#2D4A3E] font-medium underline">Go Pro</Link> for the full list.</div>}
            </Card>

            {companies.length > 0 && (
              <Card className="mt-6 border-[#E2E8E4] overflow-hidden" data-testid="companies-table-card">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8E4]">
                  <div className="text-sm text-[#5C6661]">
                    <span className="font-medium text-[#121413]" data-testid="selected-count">{selectedIds.length}</span> of {companies.length} selected
                    {hiddenCount > 0 && !isPro && <span className="ml-3 text-[#5C6661]">· {hiddenCount} more locked</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="toggle-all-btn">{selectedIds.length === companies.length ? "Deselect all" : "Select all"}</Button>
                    <Button onClick={goReview} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" size="sm" data-testid="review-btn">Review & send <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                  </div>
                </div>
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead className="hidden md:table-cell">Industry</TableHead>
                    <TableHead className="hidden lg:table-cell">Location</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {companies.map((c) => (
                      <TableRow key={c.id} data-testid={`company-row-${c.id}`} className="hover:bg-[#F4F7F5]">
                        <TableCell><Checkbox checked={!!selected[c.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.id]: !!v }))} data-testid={`select-company-${c.id}`} /></TableCell>
                        <TableCell>
                          <Link to={`/app/companies/${c.id}`} className="flex items-center gap-2 group">
                            <Building2 className="w-4 h-4 text-[#5C6661]" />
                            <div>
                              <div className="font-medium text-[#121413] group-hover:text-[#2D4A3E]">{c.name}</div>
                              {c.reason && <div className="text-xs text-[#5C6661] max-w-md">{c.reason}</div>}
                            </div>
                          </Link>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-[#5C6661]">{c.industry}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-[#5C6661]">{c.location}</TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-col gap-0.5">
                            {(c.emails || []).slice(0, 2).map((e) => (<span key={e} className="text-[#2D4A3E] font-mono">{e}</span>))}
                            {c.emails_verified && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-[#2D4A3E] mt-0.5" data-testid={`verified-badge-${c.id}`}>
                                <ShieldCheck className="w-3 h-3" /> Verified from site
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {c.website && <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="text-[#5C6661] hover:text-[#2D4A3E]"><ExternalLink className="w-3.5 h-3.5" /></a>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {!isPro && hiddenCount > 0 && (
                  <div className="p-6 border-t border-[#E2E8E4] bg-[#F2F4F3] flex items-center justify-between flex-wrap gap-3" data-testid="paywall-banner">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2D4A3E]/10 flex items-center justify-center"><Lock className="w-4 h-4 text-[#2D4A3E]" /></div>
                      <div>
                        <div className="font-medium">{hiddenCount} more matches locked</div>
                        <div className="text-xs text-[#5C6661]">Unlock the full shortlist with JobKnocker Pro — £4.99/month.</div>
                      </div>
                    </div>
                    <Link to="/pricing" data-testid="paywall-cta"><Button className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]"><Crown className="w-4 h-4 mr-2" />Unlock with Pro</Button></Link>
                  </div>
                )}
              </Card>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
